import React, { useState } from "react";

import { CheckIcon, GlobeIcon } from "../../icons_v2/general";
import { Menu } from "../../overlay/Menu";
import { HStack, Pressable, Text, VStack } from "../../primitive";

export type SupportedLanguage = "en" | "fr";

type LanguageSelectProps = {
  value: SupportedLanguage;
  onChange: (language: SupportedLanguage) => void;
  accessibilityLabel: string;
  optionAccessibilityLabels: Record<SupportedLanguage, string>;
  testID?: string;
};

const languageOptions: { label: string; value: SupportedLanguage }[] = [
  { label: "English", value: "en" },
  { label: "Français", value: "fr" },
];

export const LanguageSelect = ({
  value,
  onChange,
  accessibilityLabel,
  optionAccessibilityLabels,
  testID = "language-select",
}: LanguageSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Menu
      overrideIsOpen={isOpen}
      onClose={() => setIsOpen(false)}
      openPosition="bottom-end"
      verticalOffset={4}
      neverOpenAsActionSheet
      trigger={({ onPress }) => (
        <Pressable
          alignItems="center"
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="button"
          backgroundColor={
            isOpen ? "button-white-surface-pressed" : "surface-primary"
          }
          borderRadius="2"
          height={32}
          justifyContent="center"
          onPress={() => {
            setIsOpen((open) => !open);
            onPress();
          }}
          paddingHorizontal={3}
          testID={testID}
          _hover={{ backgroundColor: "button-white-surface-hover" }}
          _pressed={{ backgroundColor: "button-white-surface-pressed" }}
        >
          <HStack alignItems="center" gap={2}>
            <GlobeIcon accessible={false} color="text-primary" />
            <Text color="text-primary" variant="mainSmallSemiBold">
              {value.toUpperCase()}
            </Text>
          </HStack>
        </Pressable>
      )}
      content={({ closeMenu }) => (
        <Menu.Content size="regular" testID={`${testID}-menu`}>
          <VStack>
            {languageOptions.map((option) => {
              const isSelected = option.value === value;

              return (
                <Pressable
                  accessibilityLabel={optionAccessibilityLabels[option.value]}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected: isSelected }}
                  backgroundColor={
                    isSelected ? "surface-secondary" : "surface-primary"
                  }
                  borderRadius="2"
                  height={48}
                  justifyContent="center"
                  key={option.value}
                  onPress={() => {
                    onChange(option.value);
                    closeMenu();
                    setIsOpen(false);
                  }}
                  paddingHorizontal={4}
                  testID={`${testID}-${option.value}`}
                  _hover={{ backgroundColor: "surface-secondary" }}
                  _pressed={{ backgroundColor: "surface-secondary" }}
                >
                  <HStack alignItems="center" justifyContent="space-between">
                    <Text color="text-primary" variant="mainSmallSemiBold">
                      {option.label}
                    </Text>
                    {isSelected ? (
                      <CheckIcon accessible={false} color="text-primary" />
                    ) : null}
                  </HStack>
                </Pressable>
              );
            })}
          </VStack>
        </Menu.Content>
      )}
    />
  );
};
